import type { CSSProperties, ReactElement, ReactNode } from 'react'

export interface Column {
  id: string
  label: string
  /** Absent grows to fill; a width holds a column of figures to one edge. */
  width?: string
  align?: 'left' | 'right'
}

/**
 * A table of rows all the same height, ruled between and never around.
 *
 * Divs rather than a `<table>` would have been simpler to lay out and wrong:
 * twelve rows of kroner across seven columns is a table, and a reader arriving
 * with a screen reader is entitled to be told which column a figure is in.
 */
export function DataTable({
  columns,
  children,
  label,
  dense,
  total,
}: {
  columns: readonly Column[]
  children: ReactNode
  label?: string
  /** The 31px row the return table uses, against the 40px row of the rest. */
  dense?: boolean
  /**
   * The row the whole table adds up to: taller than the rest, under a rule
   * heavier than the ones between rows, and in the document as a `tfoot` so it
   * is a summary of the table rather than one more of its rows.
   */
  total?: ReactNode
}): ReactElement {
  return (
    <table className="nw-data" data-dense={dense === true ? 'true' : undefined}>
      {label !== undefined && <caption className="nw-sr-only">{label}</caption>}
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.id}
              scope="col"
              className={column.align === 'right' ? 'nw-num' : undefined}
              style={column.width === undefined ? undefined : ({ width: column.width } as CSSProperties)}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
      {total !== undefined && <tfoot>{total}</tfoot>}
    </table>
  )
}

/** A dot of the series colour, against the row it names. */
export function Swatch({ color }: { color: string }): ReactElement {
  return <span className="nw-swatch" style={{ background: color } as CSSProperties} aria-hidden="true" />
}

export interface Segment {
  id: string
  label: string
  share: number
  color: string
}

/**
 * The whole, in one rounded bar.
 *
 * Shares are laid out as a grid of fractions rather than as percentage widths,
 * so a rounding error cannot leave a sliver of card showing at the end of it.
 */
export function StackedBar({ segments, label }: { segments: readonly Segment[]; label: string }): ReactElement {
  return (
    <div
      className="nw-stacked"
      role="img"
      aria-label={label}
      style={{ gridTemplateColumns: segments.map((s) => `${Math.max(s.share, 0)}fr`).join(' ') } as CSSProperties}
    >
      {segments.map((segment) => (
        <span key={segment.id} style={{ background: segment.color } as CSSProperties} />
      ))}
    </div>
  )
}
